import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Sun, Moon, Star, ArrowLeft, ArrowRight, Loader2, Music, CreditCard, User, Check, FileText, UserPlus, CheckCircle } from 'lucide-react';
import SenArtisteLogo from '../components/SenArtisteLogo';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ArtistOnboarding = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme, toggleTheme } = useTheme();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [cguOpen, setCguOpen] = useState(false);
  const [cguText, setCguText] = useState('');
  const [apporteurInfo, setApporteurInfo] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  
  const [formData, setFormData] = useState({
    // Étape 1: Identité
    prenom: '',
    nom: '',
    nomDeScene: '',
    slug: '',
    // Étape 2: Contact & Bio
    emailContact: '',
    telephoneContact: '',
    bio: '',
    // Étape 3: Compte
    email: '',
    password: '',
    confirmPassword: '',
    // Étape 4: Paiements & Parrainage
    sepa_payments_enabled: false,
    fcfa_payments_enabled: true,  // Activé par défaut pour les artistes africains
    numeroOrangeMoney: '',
    numeroWave: '',
    apporteurCode: '',
    // Étape 5: CGU
    acceptCGU: false,
  });

  const [errors, setErrors] = useState({});

  // Charger le code apporteur depuis l'URL et les CGU au montage
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, apporteurCode: refCode }));
      validateApporteurCode(refCode);
    }
    
    // Charger les CGU
    fetchCGU();
  }, [searchParams]);

  const fetchCGU = async () => {
    try {
      const response = await axios.get(`${API_URL}/cgu`);
      setCguText(response.data.text);
    } catch (error) {
      console.error('Error fetching CGU:', error);
    }
  };

  const validateApporteurCode = async (code) => {
    if (!code || code.length < 4) {
      setApporteurInfo(null);
      return;
    }
    
    setValidatingCode(true);
    try {
      const response = await axios.get(`${API_URL}/validate-apporteur-code/${code}`);
      setApporteurInfo(response.data);
      toast.success(`Code valide ! Parrainé par ${response.data.apporteurName}`);
    } catch (error) {
      setApporteurInfo(null);
      if (code.length >= 6) {
        toast.error('Code apporteur invalide ou inactif');
      }
    } finally {
      setValidatingCode(false);
    }
  };

  const validateStep = (currentStep) => {
    const newErrors = {};
    
    if (currentStep === 1) {
      if (!formData.prenom.trim()) newErrors.prenom = 'Prénom requis';
      if (!formData.nom.trim()) newErrors.nom = 'Nom requis';
      if (!formData.nomDeScene.trim()) newErrors.nomDeScene = 'Nom de scène requis';
      if (!formData.slug.trim()) {
        newErrors.slug = 'Slug requis';
      } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
        newErrors.slug = 'Le slug ne doit contenir que des lettres minuscules, chiffres et tirets';
      }
    }
    
    if (currentStep === 2) {
      if (!formData.emailContact.trim()) newErrors.emailContact = 'Email de contact requis';
    }
    
    if (currentStep === 3) {
      if (!formData.email.trim()) {
        newErrors.email = 'Email requis';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email invalide';
      }
      if (!formData.password) {
        newErrors.password = 'Mot de passe requis';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Minimum 6 caractères';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }
    }
    
    if (currentStep === 5) {
      if (!formData.acceptCGU) {
        newErrors.acceptCGU = 'Vous devez accepter les CGU pour continuer';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const generateSlug = (nomDeScene) => {
    return nomDeScene
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNomDeSceneChange = (value) => {
    setFormData(prev => ({
      ...prev,
      nomDeScene: value,
      slug: generateSlug(value),
    }));
  };

  const handleApporteurCodeChange = (value) => {
    const upperCode = value.toUpperCase();
    setFormData(prev => ({ ...prev, apporteurCode: upperCode }));
    
    // Valider après un délai
    if (upperCode.length >= 6) {
      validateApporteurCode(upperCode);
    } else {
      setApporteurInfo(null);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/onboarding/artist`, {
        slug: formData.slug,
        prenom: formData.prenom,
        nom: formData.nom,
        nomDeScene: formData.nomDeScene,
        emailContact: formData.emailContact,
        telephoneContact: formData.telephoneContact,
        bio: formData.bio,
        email: formData.email,
        password: formData.password,
        sepa_payments_enabled: formData.sepa_payments_enabled,
        fcfa_payments_enabled: formData.fcfa_payments_enabled,
        numeroOrangeMoney: formData.numeroOrangeMoney,
        numeroWave: formData.numeroWave,
        apporteurCode: formData.apporteurCode || null,
        acceptCGU: formData.acceptCGU,
      });
      
      const { token, artistSlug } = response.data;
      localStorage.setItem('token', token);
      
      toast.success(response.data.message || 'Compte artiste créé avec succès!');
      navigate(`/${artistSlug}/management`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création du compte';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, title: 'Identité', icon: <User className="w-5 h-5" /> },
    { num: 2, title: 'Contact', icon: <Music className="w-5 h-5" /> },
    { num: 3, title: 'Compte', icon: <Star className="w-5 h-5" /> },
    { num: 4, title: 'Paiements', icon: <CreditCard className="w-5 h-5" /> },
    { num: 5, title: 'CGU', icon: <FileText className="w-5 h-5" /> },
  ];

  return (
    <div className="min-h-screen py-8 px-4 hero-gradient">
      {/* Background */}
      <div 
        className="fixed inset-0 opacity-10 -z-10"
        style={{
          backgroundImage: 'url(/images/artist-placeholder.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* Theme Toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="fixed top-4 right-4 z-50"
        data-testid="theme-toggle"
      >
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </Button>
      
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/')}
        className="fixed top-4 left-4 z-50"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour
      </Button>

      <div className="max-w-2xl mx-auto pt-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <SenArtisteLogo className="w-10 h-10" />
            <span className="font-syne font-bold text-3xl">Sen Artiste</span>
          </Link>
          <h1 className="font-syne text-2xl font-bold mt-4">Créer votre espace artiste</h1>
          <p className="text-muted-foreground">Rejoignez la communauté d'artistes</p>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8 relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-muted -z-10">
            <div 
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${((step - 1) / 4) * 100}%` }}
            />
          </div>
          {steps.map((s) => (
            <div 
              key={s.num}
              className={`flex flex-col items-center ${step >= s.num ? 'text-primary' : 'text-muted-foreground'}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${
                step > s.num ? 'bg-primary text-primary-foreground' : 
                step === s.num ? 'bg-primary text-primary-foreground' : 
                'bg-muted text-muted-foreground'
              }`}>
                {step > s.num ? <Check className="w-5 h-5" /> : s.icon}
              </div>
              <span className="text-xs font-medium hidden sm:block">{s.title}</span>
            </div>
          ))}
        </div>

        {/* Form Card */}
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
        >
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-syne">
                {step === 1 && 'Votre identité artistique'}
                {step === 2 && 'Contact & Biographie'}
                {step === 3 && 'Créer votre compte'}
                {step === 4 && 'Options de paiement & Parrainage'}
                {step === 5 && 'Conditions Générales d\'Utilisation'}
              </CardTitle>
              <CardDescription>
                {step === 1 && 'Comment voulez-vous être présenté à vos fans?'}
                {step === 2 && 'Comment vos fans peuvent-ils vous contacter?'}
                {step === 3 && 'Informations de connexion à votre espace'}
                {step === 4 && 'Configurer les moyens de recevoir vos revenus'}
                {step === 5 && 'Veuillez lire et accepter nos conditions'}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Step 1: Identité */}
              {step === 1 && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prénom *</Label>
                      <Input
                        value={formData.prenom}
                        onChange={(e) => setFormData(p => ({ ...p, prenom: e.target.value }))}
                        className={errors.prenom ? 'border-red-500' : ''}
                        data-testid="onboard-prenom"
                      />
                      {errors.prenom && <p className="text-xs text-red-500">{errors.prenom}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Nom *</Label>
                      <Input
                        value={formData.nom}
                        onChange={(e) => setFormData(p => ({ ...p, nom: e.target.value }))}
                        className={errors.nom ? 'border-red-500' : ''}
                        data-testid="onboard-nom"
                      />
                      {errors.nom && <p className="text-xs text-red-500">{errors.nom}</p>}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Nom de scène *</Label>
                    <Input
                      value={formData.nomDeScene}
                      onChange={(e) => handleNomDeSceneChange(e.target.value)}
                      placeholder="Ex: Youssou"
                      className={errors.nomDeScene ? 'border-red-500' : ''}
                      data-testid="onboard-nomDeScene"
                    />
                    {errors.nomDeScene && <p className="text-xs text-red-500">{errors.nomDeScene}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>URL de votre page (slug) *</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-sm">senartiste.com/</span>
                      <Input
                        value={formData.slug}
                        onChange={(e) => setFormData(p => ({ ...p, slug: e.target.value.toLowerCase() }))}
                        placeholder="votre-nom"
                        className={errors.slug ? 'border-red-500' : ''}
                        data-testid="onboard-slug"
                      />
                    </div>
                    {errors.slug && <p className="text-xs text-red-500">{errors.slug}</p>}
                    <p className="text-xs text-muted-foreground">
                      Cette URL sera utilisée pour accéder à votre fanpage publique
                    </p>
                  </div>
                </>
              )}

              {/* Step 2: Contact & Bio */}
              {step === 2 && (
                <>
                  <div className="space-y-2">
                    <Label>Email de contact public *</Label>
                    <Input
                      type="email"
                      value={formData.emailContact}
                      onChange={(e) => setFormData(p => ({ ...p, emailContact: e.target.value }))}
                      placeholder="contact@votre-nom.com"
                      className={errors.emailContact ? 'border-red-500' : ''}
                      data-testid="onboard-emailContact"
                    />
                    {errors.emailContact && <p className="text-xs text-red-500">{errors.emailContact}</p>}
                    <p className="text-xs text-muted-foreground">
                      Cet email sera visible sur votre fanpage
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Téléphone de contact</Label>
                    <Input
                      value={formData.telephoneContact}
                      onChange={(e) => setFormData(p => ({ ...p, telephoneContact: e.target.value }))}
                      placeholder="+221 77 xxx xx xx"
                      data-testid="onboard-telephone"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Biographie</Label>
                    <Textarea
                      value={formData.bio}
                      onChange={(e) => setFormData(p => ({ ...p, bio: e.target.value }))}
                      placeholder="Présentez-vous à vos fans..."
                      rows={4}
                      data-testid="onboard-bio"
                    />
                  </div>
                </>
              )}

              {/* Step 3: Compte */}
              {step === 3 && (
                <>
                  <div className="p-4 bg-muted/50 rounded-lg text-sm">
                    <p className="font-medium mb-1">Ces identifiants vous permettront de vous connecter</p>
                    <p className="text-muted-foreground">
                      Vous pourrez ensuite accéder à votre tableau de bord pour gérer vos événements, médias et fans.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Email de connexion *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                      placeholder="votre@email.com"
                      className={errors.email ? 'border-red-500' : ''}
                      data-testid="onboard-email"
                    />
                    {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Mot de passe *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                      placeholder="••••••••"
                      className={errors.password ? 'border-red-500' : ''}
                      data-testid="onboard-password"
                    />
                    {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Confirmer le mot de passe *</Label>
                    <Input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData(p => ({ ...p, confirmPassword: e.target.value }))}
                      placeholder="••••••••"
                      className={errors.confirmPassword ? 'border-red-500' : ''}
                      data-testid="onboard-confirmPassword"
                    />
                    {errors.confirmPassword && <p className="text-xs text-red-500">{errors.confirmPassword}</p>}
                  </div>
                </>
              )}

              {/* Step 4: Paiements & Parrainage */}
              {step === 4 && (
                <>
                  {/* Code Apporteur */}
                  <div className="p-4 border border-primary/30 bg-primary/5 rounded-lg">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-primary" />
                      Code parrainage (optionnel)
                    </h4>
                    <div className="space-y-2">
                      <Label>Code apporteur</Label>
                      <div className="flex gap-2">
                        <Input
                          value={formData.apporteurCode}
                          onChange={(e) => handleApporteurCodeChange(e.target.value)}
                          placeholder="APRT-XXXXXX"
                          className={`font-mono ${apporteurInfo ? 'border-green-500' : ''}`}
                          data-testid="onboard-apporteurCode"
                        />
                        {validatingCode && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
                        {apporteurInfo && <CheckCircle className="w-5 h-5 text-green-500" />}
                      </div>
                      {apporteurInfo && (
                        <p className="text-sm text-green-600">
                          ✓ Parrainé par {apporteurInfo.apporteurName}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Si vous avez un code parrainage, entrez-le ici. Ce n'est pas obligatoire.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Paiements FCFA */}
                    <div className="p-4 border border-border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium flex items-center gap-2">
                          <span className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-600 text-xs">F</span>
                          Paiements en FCFA (Wave / Orange Money)
                        </h4>
                        <Switch
                          checked={formData.fcfa_payments_enabled}
                          onCheckedChange={(v) => setFormData(p => ({ ...p, fcfa_payments_enabled: v }))}
                          data-testid="onboard-fcfa-switch"
                        />
                      </div>
                      
                      {formData.fcfa_payments_enabled ? (
                        <div className="space-y-3">
                          <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-3">
                            <p className="text-sm text-green-600">
                              ✓ Les paiements Wave et Orange Money seront activés pour vos événements en FCFA.
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label>Numéro Orange Money</Label>
                            <Input
                              value={formData.numeroOrangeMoney}
                              onChange={(e) => setFormData(p => ({ ...p, numeroOrangeMoney: e.target.value }))}
                              placeholder="+221 77 xxx xx xx"
                              data-testid="onboard-orangeMoney"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Numéro Wave</Label>
                            <Input
                              value={formData.numeroWave}
                              onChange={(e) => setFormData(p => ({ ...p, numeroWave: e.target.value }))}
                              placeholder="+221 76 xxx xx xx"
                              data-testid="onboard-wave"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            ℹ️ Ces numéros seront utilisés pour recevoir vos paiements lors des retraits.
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Activez cette option si vous souhaitez recevoir des paiements en FCFA via Wave ou Orange Money.
                        </p>
                      )}
                    </div>
                    
                    {/* Paiements EUR */}
                    <div className="p-4 border border-border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium flex items-center gap-2">
                          <span className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-600 text-xs">€</span>
                          Paiements en EUR (Stripe)
                        </h4>
                        <Switch
                          checked={formData.sepa_payments_enabled}
                          onCheckedChange={(v) => setFormData(p => ({ ...p, sepa_payments_enabled: v }))}
                          data-testid="onboard-sepa-switch"
                        />
                      </div>
                      
                      {formData.sepa_payments_enabled ? (
                        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                          <p className="text-sm text-blue-600">
                            ✓ Les paiements par carte bancaire (Stripe) seront activés.
                            <br />
                            <span className="text-xs">Vous pourrez compléter la configuration Stripe depuis votre tableau de bord après inscription.</span>
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Activez cette option si vous souhaitez recevoir des paiements par carte bancaire en EUR via Stripe.
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Step 5: CGU */}
              {step === 5 && (
                <>
                  <div className="border border-border rounded-lg p-4 max-h-64 overflow-y-auto bg-muted/30">
                    <pre className="text-sm whitespace-pre-wrap font-sans">{cguText || 'Chargement des CGU...'}</pre>
                  </div>
                  
                  <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                    <Checkbox
                      id="acceptCGU"
                      checked={formData.acceptCGU}
                      onCheckedChange={(checked) => setFormData(p => ({ ...p, acceptCGU: checked }))}
                      data-testid="onboard-acceptCGU"
                    />
                    <div className="space-y-1">
                      <label htmlFor="acceptCGU" className="text-sm font-medium cursor-pointer">
                        J'ai lu et j'accepte les Conditions Générales d'Utilisation *
                      </label>
                      <p className="text-xs text-muted-foreground">
                        En cochant cette case, vous acceptez nos conditions d'utilisation, notre politique de confidentialité et les tarifs applicables.
                      </p>
                      {errors.acceptCGU && <p className="text-xs text-red-500">{errors.acceptCGU}</p>}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-primary/10 rounded-lg">
                    <h4 className="font-medium mb-2">Résumé de votre inscription</h4>
                    <ul className="text-sm space-y-1 text-muted-foreground">
                      <li>• Nom de scène : <span className="text-foreground font-medium">{formData.nomDeScene}</span></li>
                      <li>• URL : <span className="text-foreground font-medium">sen-artiste.com/{formData.slug}</span></li>
                      <li>• Email : <span className="text-foreground font-medium">{formData.email}</span></li>
                      <li>• Paiements FCFA (Wave/OM) : <span className={`font-medium ${formData.fcfa_payments_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>{formData.fcfa_payments_enabled ? 'Activé' : 'Désactivé'}</span></li>
                      <li>• Paiements EUR (Stripe) : <span className={`font-medium ${formData.sepa_payments_enabled ? 'text-blue-600' : 'text-muted-foreground'}`}>{formData.sepa_payments_enabled ? 'Activé' : 'Désactivé'}</span></li>
                      {apporteurInfo && (
                        <li>• Parrainé par : <span className="text-green-600 font-medium">{apporteurInfo.apporteurName}</span></li>
                      )}
                    </ul>
                  </div>
                </>
              )}

              {/* Navigation */}
              <div className="flex justify-between pt-4">
                {step > 1 ? (
                  <Button variant="outline" onClick={handleBack} data-testid="onboard-back">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour
                  </Button>
                ) : (
                  <div />
                )}
                
                {step < 5 ? (
                  <Button onClick={handleNext} className="btn-primary" data-testid="onboard-next">
                    Suivant
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit} 
                    className="btn-primary"
                    disabled={loading || !formData.acceptCGU}
                    data-testid="onboard-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Création...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Créer mon espace
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Login Link */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Déjà un compte?{' '}
          <Link to="/login" className="text-primary hover:underline">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ArtistOnboarding;
